import React, { useEffect, useRef, useState } from 'react';
import './Auth.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

const QUALIFICATIONS = [
  'High School / 10th', 'Intermediate / 12th', 'Diploma',
  'Undergraduate (Pursuing)', "Graduate / Bachelor's Degree",
  "Post Graduate / Master's Degree", 'PhD / Doctorate',
  'Certification Course', 'Other',
];
const STREAMS = [
  'Computer Science / IT', 'Engineering', 'Commerce', 'Science',
  'Arts / Humanities', 'Management / Business', 'Medical / Healthcare',
  'Law', 'Education / Teaching', 'Design / UI-UX', 'Finance / Accounting',
  'Marketing', 'Data Science / AI', 'Cybersecurity',
  'Hospitality / Hotel Management', 'Animation / Multimedia',
  'Agriculture', 'Architecture', 'Psychology', 'Other',
];
const EXPERIENCES = [
  'Fresher', 'Less than 1 year', '1-2 years',
  '2-5 years', '5-10 years', '10+ years', 'Other',
];

function IconEye()    { return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function IconEyeOff() { return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>; }
function IconChevron() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><polyline points="6 9 12 15 18 9" /></svg>; }
function IconSave()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>; }

function Field({ id, label, children, error, optional }) {
  return (
    <div className="auth-field">
      <label className="auth-label" htmlFor={id}>
        {label}
        {optional && <span className="auth-optional"> (optional)</span>}
      </label>
      {children}
      {error && <span className="auth-error" role="alert">{error}</span>}
    </div>
  );
}

function SelectField({ id, label, value, onChange, options, error, placeholder, optional }) {
  return (
    <Field id={id} label={label} error={error} optional={optional}>
      <div className="auth-select-wrap">
        <select id={id} className={`auth-select${error ? ' has-error' : ''}`}
          value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <span className="auth-select-chevron"><IconChevron /></span>
      </div>
    </Field>
  );
}

/**
 * ProfileSettings — modal for editing user profile and password.
 * Props:
 *   currentUser   : object  — the currently logged-in user
 *   token         : string  — JWT
 *   onUpdate      : (updatedUser) => void  — called after successful save
 *   onClose       : () => void
 *   isDarkMode    : boolean
 */
export function ProfileSettings({ currentUser, token, onUpdate, onClose, isDarkMode }) {
  const [form, setForm] = useState({
    firstName:          currentUser?.firstName || '',
    middleName:         currentUser?.middleName || '',
    lastName:           currentUser?.lastName || '',
    dateOfBirth:        currentUser?.dateOfBirth || '',
    email:              currentUser?.email || '',
    qualification:      currentUser?.qualification || '',
    customQualification: currentUser?.customQualification || '',
    stream:             currentUser?.stream || '',
    customStream:       currentUser?.customStream || '',
    experience:         currentUser?.experience || '',
    customExperience:   currentUser?.customExperience || '',
    // Password change
    currentPassword:    '',
    newPassword:        '',
    confirmNewPassword: '',
  });
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [showCurPw, setShowCurPw]   = useState(false);
  const [showNewPw, setShowNewPw]   = useState(false);
  const [showCNPw, setShowCNPw]     = useState(false);
  const modalRef = useRef(null);
  const toastTimer = useRef(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Cleanup toast timer
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (apiError) setApiError('');
    if (success) setSuccess('');
  }

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.lastName.trim())  e.lastName  = 'Last name is required.';
    if (!form.email.trim())     e.email     = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (form.qualification === 'Other' && !form.customQualification.trim())
      e.customQualification = 'Please specify your qualification.';
    if (form.stream === 'Other' && !form.customStream.trim())
      e.customStream = 'Please specify your stream.';
    if (form.experience === 'Other' && !form.customExperience.trim())
      e.customExperience = 'Please specify your experience.';
    // Password section (only if newPassword is provided)
    if (form.newPassword) {
      if (!form.currentPassword) e.currentPassword = 'Enter your current password.';
      if (form.newPassword.length < 6) e.newPassword = 'New password must be at least 6 characters.';
      if (form.newPassword !== form.confirmNewPassword) e.confirmNewPassword = 'Passwords do not match.';
    }
    return e;
  }

  async function handleSave(ev) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setApiError('');
    try {
      const payload = {
        firstName:          form.firstName.trim(),
        middleName:         form.middleName.trim(),
        lastName:           form.lastName.trim(),
        dateOfBirth:        form.dateOfBirth,
        email:              form.email.trim(),
        qualification:      form.qualification,
        customQualification: form.customQualification.trim(),
        stream:             form.stream,
        customStream:       form.customStream.trim(),
        experience:         form.experience,
        customExperience:   form.customExperience.trim(),
      };
      if (form.newPassword) {
        payload.currentPassword = form.currentPassword;
        payload.newPassword     = form.newPassword;
      }

      const res  = await fetch(`${API_BASE}/auth/profile`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body:    JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Update failed. Please try again.');

      // Reset password fields
      setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmNewPassword: '' }));
      setSuccess('✓ Profile updated successfully!');
      toastTimer.current = setTimeout(() => setSuccess(''), 3500);
      onUpdate(data.user);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function pwField(id, label, field, show, setShow, autoComplete) {
    return (
      <Field id={id} label={label} error={errors[field]}>
        <div className="auth-pw-wrap">
          <input id={id} type={show ? 'text' : 'password'}
            className={`auth-input${errors[field] ? ' has-error' : ''}`}
            placeholder={id === 'ps-curpw' ? 'Current password' : id === 'ps-newpw' ? 'Min. 6 characters' : 'Repeat new password'}
            value={form[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            autoComplete={autoComplete}
          />
          <button type="button" className="auth-pw-toggle" onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide' : 'Show'}>
            {show ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
      </Field>
    );
  }

  return (
    <div className="ps-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ps-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Profile Settings">
        <div className="ps-header">
          <h2 className="ps-title">Profile Settings</h2>
          <button type="button" className="ps-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {apiError && <div className="auth-api-error" role="alert">⚠ {apiError}</div>}
        {success  && (
          <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.14)',
            border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', fontSize: '0.85rem' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSave} noValidate>
          {/* ── Personal Info ── */}
          <p className="ps-section-title">Personal Information</p>

          <div className="ps-form-row">
            <Field id="ps-firstname" label="First Name" error={errors.firstName}>
              <input id="ps-firstname" type="text"
                className={`auth-input${errors.firstName ? ' has-error' : ''}`}
                value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                autoComplete="given-name" required
              />
            </Field>
            <Field id="ps-middlename" label="Middle Name" optional>
              <input id="ps-middlename" type="text" className="auth-input"
                value={form.middleName} onChange={(e) => handleChange('middleName', e.target.value)}
                autoComplete="additional-name"
              />
            </Field>
          </div>

          <div className="ps-form-row">
            <Field id="ps-lastname" label="Last Name" error={errors.lastName}>
              <input id="ps-lastname" type="text"
                className={`auth-input${errors.lastName ? ' has-error' : ''}`}
                value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                autoComplete="family-name" required
              />
            </Field>
            <Field id="ps-dob" label="Date of Birth" optional>
              <input id="ps-dob" type="date" className="auth-input"
                value={form.dateOfBirth} onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                style={{ colorScheme: isDarkMode ? 'dark' : 'light' }}
              />
            </Field>
          </div>

          {/* ── Account ── */}
          <div className="ps-divider" />
          <p className="ps-section-title">Account</p>

          <Field id="ps-email" label="Email" error={errors.email}>
            <input id="ps-email" type="email"
              className={`auth-input${errors.email ? ' has-error' : ''}`}
              value={form.email} onChange={(e) => handleChange('email', e.target.value)}
              autoComplete="email" required
            />
          </Field>

          {/* ── Career Profile ── */}
          <div className="ps-divider" />
          <p className="ps-section-title">Career Profile</p>

          <SelectField id="ps-qualification" label="Highest Qualification"
            value={form.qualification} onChange={(v) => handleChange('qualification', v)}
            options={QUALIFICATIONS} error={errors.qualification}
            placeholder="— Select qualification —"
          />
          {form.qualification === 'Other' && (
            <Field id="ps-customQualification" label="Specify Qualification" error={errors.customQualification}>
              <input id="ps-customQualification" type="text" className={`auth-input${errors.customQualification ? ' has-error' : ''}`}
                placeholder="Your qualification"
                value={form.customQualification} onChange={(e) => handleChange('customQualification', e.target.value)}
              />
            </Field>
          )}

          <SelectField id="ps-stream" label="Stream / Field of Study"
            value={form.stream} onChange={(v) => handleChange('stream', v)}
            options={STREAMS} error={errors.stream}
            placeholder="— Select stream —"
          />
          {form.stream === 'Other' && (
            <Field id="ps-customStream" label="Specify Stream" error={errors.customStream}>
              <input id="ps-customStream" type="text" className={`auth-input${errors.customStream ? ' has-error' : ''}`}
                placeholder="Your stream"
                value={form.customStream} onChange={(e) => handleChange('customStream', e.target.value)}
              />
            </Field>
          )}

          <SelectField id="ps-experience" label="Work Experience"
            value={form.experience} onChange={(v) => handleChange('experience', v)}
            options={EXPERIENCES} error={errors.experience}
            placeholder="— Select experience —"
          />
          {form.experience === 'Other' && (
            <Field id="ps-customExperience" label="Specify Experience" error={errors.customExperience}>
              <input id="ps-customExperience" type="text" className={`auth-input${errors.customExperience ? ' has-error' : ''}`}
                placeholder="Your experience"
                value={form.customExperience} onChange={(e) => handleChange('customExperience', e.target.value)}
              />
            </Field>
          )}

          {/* ── Change Password ── */}
          <div className="ps-divider" />
          <p className="ps-section-title">Change Password <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted)', fontSize: '0.7rem' }}>(leave blank to keep current)</span></p>

          <div className="ps-form-row">
            {pwField('ps-curpw',  'Current Password', 'currentPassword',    showCurPw, setShowCurPw, 'current-password')}
            {pwField('ps-newpw',  'New Password',     'newPassword',        showNewPw, setShowNewPw, 'new-password')}
          </div>
          {pwField('ps-cnpw', 'Confirm New Password', 'confirmNewPassword', showCNPw, setShowCNPw, 'new-password')}

          {/* ── Actions ── */}
          <div className="ps-actions">
            <button type="button" className="ps-cancel" onClick={onClose}>Cancel</button>
            <button id="ps-save-btn" type="submit" className="ps-save" disabled={loading}>
              {loading ? (<><span className="auth-spinner" style={{ width: 14, height: 14 }} /> Saving…</>) : (<><IconSave /> Save Changes</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
